/**
 * YearlyReviewWidget - Yearly review, goals, and vision
 */

import React, { useState } from 'react';
import { Calendar } from 'lucide-react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { WidgetWrapper } from './WidgetWrapper';
import ReviewModal from '../ReviewModal';
import type { YearlyReviewWidgetConfig } from '../../types/widgets';
import type { YearUpdate } from '../../types';
import { yearsAPI } from '../../services/api';

interface YearlyReviewWidgetProps {
  config: YearlyReviewWidgetConfig;
  widgetId: string;
  windowId: string;
}

// eslint-disable-next-line @typescript-eslint/no-unused-vars
export function YearlyReviewWidget({ config, widgetId, windowId }: YearlyReviewWidgetProps) {
  const year = new Date().getFullYear();
  const [expanded, setExpanded] = useState(false);
  const queryClient = useQueryClient();

  const { data: currentYear } = useQuery({
    queryKey: ['years', 'current'],
    queryFn: () => yearsAPI.getCurrent(),
  });

  const updateYearMutation = useMutation({
    mutationFn: ({ yearId, data }: { yearId: string; data: YearUpdate }) =>
      yearsAPI.update(yearId, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['years'] });
    },
  });

  const completeReviewMutation = useMutation({
    mutationFn: (yearId: string) => yearsAPI.completeReview(yearId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['years'] });
    },
  });

  const handleStartReview = () => {
    setExpanded(true);
  };

  return (
    <>
    <WidgetWrapper title={`${year} Review`} widgetId={widgetId} windowId={windowId} onHeaderClick={() => setExpanded(true)}>
      <div className="p-3 h-full overflow-auto">
        <div className="flex flex-col items-center justify-center h-32 text-gray-400">
          <Calendar className="w-10 h-10 mb-3" />
          <p className="text-sm font-medium">Yearly Review</p>
          <p className="text-xs mt-1">Set your vision and goals for {year}</p>
          <button
            onClick={handleStartReview}
            className="mt-3 px-3 py-1.5 text-xs bg-blue-500 text-white rounded hover:bg-blue-600 transition-colors"
          >
            Start Yearly Review
          </button>
        </div>
      </div>
    </WidgetWrapper>

    {expanded && currentYear && (
      <ReviewModal
        isOpen
        onClose={() => setExpanded(false)}
        reviewType="yearly"
        periodLabel={String(currentYear.year)}
        reflections={currentYear.reflections}
        goals={currentYear.goals}
        vision={currentYear.vision}
        values={currentYear.values}
        onSave={(data) =>
          updateYearMutation.mutate({ yearId: currentYear.id, data: data as YearUpdate })
        }
        onComplete={(data) => {
          updateYearMutation.mutate({ yearId: currentYear.id, data: { ...data, is_review_completed: true } as YearUpdate });
          completeReviewMutation.mutate(currentYear.id);
        }}
      />
    )}
    </>
  );
}
